import { useEffect, useState } from "react";
import { Plus, Search } from "lucide-react";
import { api } from "../../api/client";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Pagination, pageCount, usePageSize } from "../../components/Pagination";
import { Select } from "../../components/Select";
import { formatTaipeiDateTime } from "../../lib/datetime";
import type { Paginated, UserPick } from "../../api/types";
import { WORK_ORDER_STATUSES, WORK_ORDER_STATUS_LABEL, WORK_ORDER_STATUS_TONE } from "../../api/workorders";
import type { FormDef, WorkOrderSummary } from "../../api/workorders";
import { loadWorkOrderView, subscribeWorkOrderView } from "./workOrderViews";
import type { WorkOrderView } from "./workOrderViews";

// 工單列表(規格 §8)。篩選送給後端(與報價單列表同一個理由:一頁只有 20 筆,在那 20 筆上篩會篩出假的「沒有」)。
// 不用 react-query:與報價單列表同一個慣例(useEffect + api),測試也不必包 Provider。

const ALL = "";
const HEADERS = ["工單號", "種類", "品名", "承辦人", "狀態", "交件日期", "更新時間"];

export interface WorkOrderListProps {
  onOpen: (id: number) => void;
  onCreate: () => void;
}

export function WorkOrderList({ onOpen, onCreate }: WorkOrderListProps) {
  const [keyword, setKeyword] = useState("");
  const [formType, setFormType] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [handlerId, setHandlerId] = useState(ALL);
  // 左側「全部 / 已結案 / 未結案」選的那一個(見 workOrderViews.ts),與上面的篩選一起送給後端。
  const [view, setView] = useState<WorkOrderView>(loadWorkOrderView);
  useEffect(() => subscribeWorkOrderView(setView), []);
  const [forms, setForms] = useState<FormDef[]>([]);
  const [users, setUsers] = useState<UserPick[]>([]);
  const [data, setData] = useState<Paginated<WorkOrderSummary> | null>(null);
  const [failed, setFailed] = useState(false);

  // 分頁。換篩選或每頁筆數就回第 1 頁,而且在 render 當下換(理由見 QuoteList)。
  const [pageSize, savePageSize] = usePageSize("nebula.pageSize.workorders");
  const [paging, setPaging] = useState({ key: "", page: 1 });
  const pagingKey = JSON.stringify([keyword.trim(), formType, status, handlerId, view, pageSize]);
  let page = paging.page;
  if (paging.key !== pagingKey) {
    page = 1;
    setPaging({ key: pagingKey, page });
  }
  const setPage = (next: number) => setPaging((prev) => ({ ...prev, page: next }));

  useEffect(() => {
    api.get<FormDef[]>("/work-orders/forms").then((got) => setForms(Array.isArray(got) ? got : [])).catch(() => undefined);
    api.get<UserPick[]>("/users/pickable").then((got) => setUsers(Array.isArray(got) ? got : [])).catch(() => undefined);
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .get<Paginated<WorkOrderSummary>>("/work-orders", {
        q: keyword.trim(), form_type: formType, status, handler_id: handlerId,
        view: view === "ALL" ? "" : view,
        page, size: pageSize,
      })
      .then((got) => {
        if (cancelled) return;
        const lastPage = pageCount(got?.total ?? 0, pageSize);
        if (page > lastPage && (got?.total ?? 0) > 0) {
          setPaging((prev) => ({ ...prev, page: lastPage }));
          return;
        }
        setData(got);
        setFailed(false);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [keyword, formType, status, handlerId, view, page, pageSize]);

  const items = Array.isArray(data?.items) ? data.items : [];

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex min-w-[12rem] flex-1 items-center gap-2 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-2.5">
          <Search aria-hidden="true" size={16} className="shrink-0 text-[var(--text-3)]" />
          <input
            type="search"
            aria-label="搜尋工單"
            placeholder="工單號或品名"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            className="h-9 w-full min-w-0 border-0 bg-transparent text-sm text-[var(--text)]"
          />
        </div>
        <Select label="種類" value={formType} onChange={(e) => setFormType(e.target.value)}
          options={[{ value: ALL, label: "全部" }, ...forms.map((f) => ({ value: f.code, label: f.name }))]} />
        <Select label="狀態" value={status} onChange={(e) => setStatus(e.target.value)}
          options={[{ value: ALL, label: "全部" }, ...WORK_ORDER_STATUSES.map((s) => ({ value: s, label: WORK_ORDER_STATUS_LABEL[s] }))]} />
        <Select label="承辦人" value={handlerId} onChange={(e) => setHandlerId(e.target.value)}
          options={[{ value: ALL, label: "全部" }, ...users.map((u) => ({ value: String(u.id), label: u.name }))]} />
        <Button type="button" variant="primary" onClick={onCreate} icon={<Plus aria-hidden="true" size={16} />}>
          新增工單
        </Button>
      </div>

      {failed ? <p role="alert" className="m-0 text-sm text-[var(--danger)]">工單列表載入失敗,請重新整理。</p> : null}

      {data && items.length === 0 ? (
        <p className="m-0 text-sm text-[var(--text-3)]">
          {keyword.trim() || formType !== ALL || status !== ALL || handlerId !== ALL || view !== "ALL"
            ? "沒有符合條件的工單" : "還沒有工單。"}
        </p>
      ) : null}

      {items.length > 0 ? (
        <div className="overflow-x-auto rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)]">
          <table className="w-full min-w-[44rem] border-collapse text-sm">
            <thead>
              <tr>
                {HEADERS.map((h) => (
                  <th key={h} scope="col" className="border-b border-[var(--border)] px-2 py-1.5 text-left font-medium text-[var(--text-2)]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-b border-[var(--border)] last:border-b-0">
                  <td className="px-2 py-1.5">
                    <button type="button" onClick={() => onOpen(row.id)} className="font-medium text-[var(--accent)] underline-offset-2 hover:underline">
                      {row.number}
                    </button>
                  </td>
                  <td className="px-2 py-1.5">{row.form_name}</td>
                  <td className="px-2 py-1.5">{row.title}</td>
                  <td className="px-2 py-1.5">{row.handler_name}</td>
                  <td className="px-2 py-1.5"><Badge tone={WORK_ORDER_STATUS_TONE[row.status]}>{row.status_label}</Badge></td>
                  <td className="px-2 py-1.5">{row.due_date ?? ""}</td>
                  <td className="px-2 py-1.5 text-[var(--text-3)]">{formatTaipeiDateTime(row.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {data && items.length > 0 ? (
        <Pagination total={data.total} page={page} size={pageSize} onPageChange={setPage} onSizeChange={savePageSize} />
      ) : null}
    </div>
  );
}
